import React, { useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

// Custom audio player shared by the IELTS and TOEFL model-test takers (Listening clips and
// Speaking prompts). A play/pause button + a progress track whose PLAYED portion is green
// (the native <audio controls> paints it grey/black and can't be reliably recoloured). Click
// the track to seek; a dropdown changes playback speed.
const fmt = (secs) => {
    const s = Math.max(0, secs | 0);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export default function AudioPlayer({ src }) {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);
    const [rate, setRate] = useState(1);
    const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

    const toggle = () => {
        const a = ref.current;
        if (!a) return;
        if (a.paused) a.play(); else a.pause();
    };
    const seek = (e) => {
        const a = ref.current;
        if (!a || !a.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        a.currentTime = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * a.duration;
    };
    const changeRate = (r) => {
        setRate(r);
        if (ref.current) ref.current.playbackRate = r;
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '1rem' }}>
            <audio
                ref={ref}
                src={src}
                onTimeUpdate={() => setCur(ref.current ? ref.current.currentTime : 0)}
                onLoadedMetadata={() => { if (ref.current) ref.current.playbackRate = rate; setDur(ref.current ? ref.current.duration || 0 : 0); }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
            />
            <button
                type="button"
                onClick={toggle}
                aria-label={playing ? 'Pause' : 'Play'}
                style={{ border: 'none', background: '#16a34a', color: '#fff', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            >
                {playing ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
            </button>
            <div
                onClick={seek}
                style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 999, cursor: 'pointer', overflow: 'hidden' }}
            >
                <div style={{ width: `${pct}%`, height: '100%', background: '#16a34a', borderRadius: 999 }} />
            </div>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {fmt(cur)} / {fmt(dur)}
            </span>
            <select
                value={rate}
                onChange={(e) => changeRate(Number(e.target.value))}
                title="Playback speed"
                aria-label="Playback speed"
                style={{ flexShrink: 0, fontSize: '0.8rem', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 4px', color: '#374151', cursor: 'pointer' }}
            >
                {[0.75, 1, 1.25, 1.5, 2].map((r) => <option key={r} value={r}>{r}×</option>)}
            </select>
        </div>
    );
}
